import {
  APP_EVENT_BUTTON_IDS as SDK_EVENT_BUTTON_IDS,
  APP_EVENTS as SDK_EVENTS,
} from '@perxis-auto/instock-sdk/lib/shared/config/events';
import { AnalyticEventBodyType, AnalyticEventType, isDefined } from '@perxis-auto/shared';

import { isMatchingRoute } from '../lib/router';

import { APP } from './app';
import { PAGES } from './pages';

/* Канал передачи событий */
export const APP_EVENTS_CHANNEL = `${APP.name}:events` as const;

/* Типы событий, происходящие в приложении */
export const APP_EVENTS = {
  Initialized: 'initialized',

  // Pages
  CarPageVisited: 'car_page_visited',
  CarsPageVisited: 'cars_page_visited',
  ComparisonPageVisited: 'comparison_page_visited',
  FavoritesPageVisited: 'favorites_page_visited',
  ModelsPageVisited: 'models_page_visited',

  // Car
  CarDataReceived: 'car_data_received',
} as const;

/**
 * Объект аналитических событий.
 * Каждое событие содержит метаинформацию, такую как категория, действие и метка.
 */
const ANALYTIC_EVENTS = {
  INITIALIZED: { event: 'customEvent', eventCategory: 'avn', eventAction: 'view', eventLabel: '' },

  // Filters & Sort
  USE_SORT: { event: 'customEvent', eventCategory: 'avn', eventAction: 'use', eventLabel: 'sort' },
  USE_FILTER: { event: 'customEvent', eventCategory: 'avn-filter', eventAction: 'use' },
  RESET_ALL_FILTERS: { event: 'customEvent', eventCategory: 'avn-filter', eventAction: 'click', eventLabel: 'reset' },

  // Car
  SELECT_CAR_TILE: { event: 'customEvent', eventCategory: 'avn', eventAction: 'click', eventLabel: 'card' },
  SELECT_CAR_TILE_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'card',
  },
  SELECT_CAR_TILE_FROM_FAVORITES: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'card',
  },

  // Model
  SELECT_MODEL_TILE: { event: 'customEvent', eventCategory: 'avn-model', eventAction: 'click', eventLabel: 'model' },
  USE_MODEL_CHECKBOX: {
    event: 'customEvent',
    eventCategory: 'avn-model',
    eventAction: 'use',
    eventLabel: 'model',
  },
  CLICK_ON_SHOW_MODELS: {
    event: 'customEvent',
    eventCategory: 'avn-model',
    eventAction: 'click',
    eventLabel: 'show',
  },
  CLICK_ON_SHOW_ALL_MODELS: {
    event: 'customEvent',
    eventCategory: 'avn-model',
    eventAction: 'click',
    eventLabel: 'show-all',
  },

  // Comparison
  CHANGE_COMPARISON_VIEW: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'use',
    eventLabel: 'only-difference',
  },
  ADD_TO_COMPARISON_FROM_FAVORITES: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'add-to-compare',
  },
  ADD_TO_COMPARISON_FROM_CARS: {
    event: 'customEvent',
    eventCategory: 'avn',
    eventAction: 'click',
    eventLabel: 'add-to-compare',
  },
  ADD_TO_COMPARISON_FROM_CAR: {
    event: 'customEvent',
    eventCategory: 'avn-card',
    eventAction: 'click',
    eventLabel: 'add-to-compare',
  },
  UNCOMPARE_FROM_CAR: {
    event: 'customEvent',
    eventCategory: 'avn-card',
    eventAction: 'click',
    eventLabel: 'remove-from-compare',
  },
  UNCOMPARE_FROM_CARS: {
    event: 'customEvent',
    eventCategory: 'avn',
    eventAction: 'click',
    eventLabel: 'remove-from-compare',
  },
  UNCOMPARE_FROM_FAVORITES: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'remove-from-compare',
  },
  UNCOMPARE_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'remove-from-compare',
  },
  UNCOMPARE_ALL: { event: 'customEvent', eventCategory: 'avn-compare', eventAction: 'click', eventLabel: 'remove-all' },

  // Favorites
  ADD_TO_FAVORITES_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'add-to-favorite',
  },
  ADD_TO_FAVORITES_FROM_CARS: {
    event: 'customEvent',
    eventCategory: 'avn',
    eventAction: 'click',
    eventLabel: 'add-to-favorite',
  },
  ADD_TO_FAVORITES_FROM_CAR: {
    event: 'customEvent',
    eventCategory: 'avn-card',
    eventAction: 'click',
    eventLabel: 'add-to-favorite',
  },
  UNFAVORITE_FROM_CAR: {
    event: 'customEvent',
    eventCategory: 'avn-card',
    eventAction: 'click',
    eventLabel: 'remove-from-favorite',
  },
  UNFAVORITE_FROM_CARS: {
    event: 'customEvent',
    eventCategory: 'avn',
    eventAction: 'click',
    eventLabel: 'remove-from-favorite',
  },
  UNFAVORITE_FROM_FAVORITES: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'remove-from-favorite',
  },
  UNFAVORITE_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'remove-from-favorite',
  },
  UNFAVORITE_ALL: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'remove-all',
  },

  // Calculators
  OPEN_CREDIT_CALC: { event: 'customEvent', eventCategory: 'avn-credit', eventAction: 'click', eventLabel: 'calc' },
  OPEN_TRADE_IN_CALC: {
    event: 'customEvent',
    eventCategory: 'avn-tradein',
    eventAction: 'click',
    eventLabel: 'evaluate',
  },

  // Navigation
  VISIT_COMPARISON: { event: 'customEvent', eventCategory: 'avn-compare', eventAction: 'visit', eventLabel: '' },
  VISIT_FAVORITES: { event: 'customEvent', eventCategory: 'avn-favorite', eventAction: 'visit', eventLabel: '' },
  VISIT_CAR: { event: 'customEvent', eventCategory: 'avn-card', eventAction: 'visit', eventLabel: '' },

  // "Go back" buttons
  GO_BACK_FROM_CAR: { event: 'customEvent', eventCategory: 'avn-card', eventAction: 'click', eventLabel: 'back' },
  GO_BACK_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'back',
  },
  GO_BACK_FROM_FAVORITES: {
    event: 'customEvent',
    eventCategory: 'avn-favorite',
    eventAction: 'click',
    eventLabel: 'back',
  },

  // Booking
  BOOKING_CAR: { event: 'customEvent', eventCategory: 'avn-card', eventAction: 'click', eventLabel: 'booking' },
  BOOKING_CAR_FROM_COMPARISON: {
    event: 'customEvent',
    eventCategory: 'avn-compare',
    eventAction: 'click',
    eventLabel: 'booking',
  },
} as const satisfies Record<string, AnalyticEventBodyType>;

/**
 * Массив аналитических событий и их правил.
 * Определяет логику соответствия события аналитическим правилам.
 */
export const APP_ANALYTIC_EVENTS: AnalyticEventType[] = [
  { rule: type => type === APP_EVENTS.Initialized, analyticEvent: ANALYTIC_EVENTS.INITIALIZED },
  {
    rule: (type, payload) => type === SDK_EVENTS.CarTileSelected && isMatchingRoute(payload.page, PAGES.Cars),
    analyticEvent: ANALYTIC_EVENTS.SELECT_CAR_TILE,
  },
  {
    rule: (type, payload) => type === SDK_EVENTS.CarTileSelected && isMatchingRoute(payload.page, PAGES.Comparison),
    analyticEvent: ANALYTIC_EVENTS.SELECT_CAR_TILE_FROM_COMPARISON,
  },
  {
    rule: (type, payload) => type === SDK_EVENTS.CarTileSelected && isMatchingRoute(payload.page, PAGES.Favorites),
    analyticEvent: ANALYTIC_EVENTS.SELECT_CAR_TILE_FROM_FAVORITES,
  },
  { rule: type => type === SDK_EVENTS.ModelTileSelected, analyticEvent: ANALYTIC_EVENTS.SELECT_MODEL_TILE },
  {
    rule: (type, payload) => type === SDK_EVENTS.ModelTileCheckboxClicked && payload.checked === true,
    analyticEvent: ANALYTIC_EVENTS.USE_MODEL_CHECKBOX,
  },
  {
    rule: (type, payload) => type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.SHOW_ALL_MODELS,
    analyticEvent: ANALYTIC_EVENTS.CLICK_ON_SHOW_ALL_MODELS,
  },
  {
    rule: (type, payload) => type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.SHOW_MODELS,
    analyticEvent: ANALYTIC_EVENTS.CLICK_ON_SHOW_MODELS,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_COMPARISON && isMatchingRoute(payload.path, PAGES.Favorites),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_COMPARISON_FROM_FAVORITES,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_COMPARISON && isMatchingRoute(payload.path, PAGES.Cars),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_COMPARISON_FROM_CARS,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_COMPARISON && isMatchingRoute(payload.path, PAGES.Car),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_COMPARISON_FROM_CAR,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_COMPARISON && isMatchingRoute(payload.path, PAGES.Car),
    analyticEvent: ANALYTIC_EVENTS.UNCOMPARE_FROM_CAR,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_COMPARISON && isMatchingRoute(payload.path, PAGES.Cars),
    analyticEvent: ANALYTIC_EVENTS.UNCOMPARE_FROM_CARS,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_COMPARISON && isMatchingRoute(payload.path, PAGES.Comparison),
    analyticEvent: ANALYTIC_EVENTS.UNCOMPARE_FROM_COMPARISON,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_COMPARISON && isMatchingRoute(payload.path, PAGES.Favorites),
    analyticEvent: ANALYTIC_EVENTS.UNCOMPARE_FROM_FAVORITES,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_ALL_FROM_COMPARISON,
    analyticEvent: ANALYTIC_EVENTS.UNCOMPARE_ALL,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_FAVORITES && isMatchingRoute(payload.path, PAGES.Comparison),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_FAVORITES_FROM_COMPARISON,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_FAVORITES && isMatchingRoute(payload.path, PAGES.Cars),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_FAVORITES_FROM_CARS,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.ADD_TO_FAVORITES && isMatchingRoute(payload.path, PAGES.Car),
    analyticEvent: ANALYTIC_EVENTS.ADD_TO_FAVORITES_FROM_CAR,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_FAVORITES && isMatchingRoute(payload.path, PAGES.Car),
    analyticEvent: ANALYTIC_EVENTS.UNFAVORITE_FROM_CAR,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_FAVORITES && isMatchingRoute(payload.path, PAGES.Cars),
    analyticEvent: ANALYTIC_EVENTS.UNFAVORITE_FROM_CARS,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_FAVORITES && isMatchingRoute(payload.path, PAGES.Favorites),
    analyticEvent: ANALYTIC_EVENTS.UNFAVORITE_FROM_FAVORITES,
  },
  {
    rule: (type, payload) =>
      payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_FROM_FAVORITES && isMatchingRoute(payload.path, PAGES.Comparison),
    analyticEvent: ANALYTIC_EVENTS.UNFAVORITE_FROM_COMPARISON,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.REMOVE_ALL_FROM_FAVORITES,
    analyticEvent: ANALYTIC_EVENTS.UNFAVORITE_ALL,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.OPEN_CREDIT_INSTOCK,
    analyticEvent: ANALYTIC_EVENTS.OPEN_CREDIT_CALC,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.OPEN_TRADE_IN_INSTOCK,
    analyticEvent: ANALYTIC_EVENTS.OPEN_TRADE_IN_CALC,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.BACK_TO_LIST_FROM_CAR_PAGE,
    analyticEvent: ANALYTIC_EVENTS.GO_BACK_FROM_CAR,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.BACK_TO_LIST_FROM_COMPARISON_PAGE,
    analyticEvent: ANALYTIC_EVENTS.GO_BACK_FROM_COMPARISON,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.BACK_TO_LIST_FROM_FAVORITES_PAGE,
    analyticEvent: ANALYTIC_EVENTS.GO_BACK_FROM_FAVORITES,
  },
  { rule: type => type === SDK_EVENTS.ComparisonViewChanged, analyticEvent: ANALYTIC_EVENTS.CHANGE_COMPARISON_VIEW },
  { rule: type => type === SDK_EVENTS.SortValueChanged, analyticEvent: ANALYTIC_EVENTS.USE_SORT },
  {
    rule: (type, payload) => type === SDK_EVENTS.FilterValueChanged && isDefined(payload.filterKey),
    analyticEvent: payload => ({
      ...ANALYTIC_EVENTS.USE_FILTER,
      eventLabel: String(payload.filterKey),
    }),
  },
  {
    rule: (type, payload) => type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.RESET_FILTERS,
    analyticEvent: ANALYTIC_EVENTS.RESET_ALL_FILTERS,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.BOOKING_FROM_CAR_PAGE,
    analyticEvent: ANALYTIC_EVENTS.BOOKING_CAR,
  },
  {
    rule: (type, payload) =>
      type === SDK_EVENTS.ButtonClicked && payload.id === SDK_EVENT_BUTTON_IDS.BOOKING_FROM_COMPARISON_PAGE,
    analyticEvent: ANALYTIC_EVENTS.BOOKING_CAR_FROM_COMPARISON,
  },
  { rule: type => type === APP_EVENTS.ComparisonPageVisited, analyticEvent: ANALYTIC_EVENTS.VISIT_COMPARISON },
  { rule: type => type === APP_EVENTS.FavoritesPageVisited, analyticEvent: ANALYTIC_EVENTS.VISIT_FAVORITES },
  { rule: type => type === APP_EVENTS.CarPageVisited, analyticEvent: ANALYTIC_EVENTS.VISIT_CAR },
] as const;
